import Broadcast from '@clearkit/icons/glyphs/Broadcast';
import BroadcastDisabled from '@clearkit/icons/glyphs/BroadcastDisabled';
import { CKSkeleton } from 'clearkit';
import capitalize from 'lodash/capitalize';

import { FieldMapping, useFieldMappingQuery } from '~/generated/graphql';

import { SubObjectType } from '../../types';

const idleColor = 'gray-500';
const successColor = 'green-500';

type SubObjectIntegrationStatusProps = {
  accountConnectorId: string;
  subObjectType: SubObjectType;
  onSubIntegrationsLoaded: (arg: FieldMapping) => void;
};

type FieldMappingParam = FieldMapping | undefined | null;

function statusColor(
  isLoading: boolean,
  fieldMapping?: FieldMappingParam,
): string {
  if (fieldMapping && fieldMapping?.enabled && !isLoading) {
    return successColor;
  } else {
    return idleColor;
  }
}

function SubObjectIntegrationStatus({
  accountConnectorId,
  subObjectType,
  onSubIntegrationsLoaded,
}: SubObjectIntegrationStatusProps) {
  const {
    data: { fieldMapping } = {},
    loading: loading,
  } = useFieldMappingQuery({
    // Using no-cache becaue we want callbacks to run every time this request is made
    fetchPolicy: 'no-cache',
    variables: {
      accountConnectorId: accountConnectorId,
      targetObjectType: capitalize(subObjectType),
    },
    onCompleted: (data) => {
      if (data.fieldMapping) {
        onSubIntegrationsLoaded(data.fieldMapping);
      }
    },
  });

  const isLoading = !fieldMapping || loading;

  return (
    <div
      className={`flex gap-1.5 min-w-[14ch] leading-none text-${statusColor(
        loading,
        fieldMapping,
      )}`}
    >
      <IndicatorIcon fieldMapping={fieldMapping} isLoading={isLoading} />
      <div className="grid gap-2">
        <CKSkeleton
          className="h-4 w-[8ch]"
          isLoading={isLoading}
          label="loading"
        >
          <h3
            className="text-sm font-medium capitalize"
            key={`subintegration-status-text-${subObjectType}-${accountConnectorId}-${loading}`}
          >
            {subObjectType}
          </h3>
        </CKSkeleton>
        <CKSkeleton
          className="h-4 w-[12ch]"
          isLoading={isLoading}
          label="loading"
        >
          <h4 className="text-xs text-gray-600">
            {fieldMapping?.fieldMappings.length
              ? `Enriching ${fieldMapping.fieldMappings.length} Fields`
              : 'Enrichment off'}
          </h4>
        </CKSkeleton>
      </div>
    </div>
  );
}

function IndicatorIcon({
  isLoading,
  fieldMapping,
}: {
  isLoading: boolean;
  fieldMapping: FieldMappingParam;
}) {
  const iconSize = 18;
  const iconProps = {
    height: iconSize,
    width: iconSize,
    className: '-mt-0.5',
  };

  return (
    <CKSkeleton className="w-5 h-5" isLoading={isLoading} label="loading">
      {fieldMapping?.enabled ? (
        <Broadcast {...iconProps} />
      ) : (
        <BroadcastDisabled {...iconProps} />
      )}
    </CKSkeleton>
  );
}

export default SubObjectIntegrationStatus;
